import { useEffect, useState } from "react";
import { promptNoticeAccess, queueBrowserNotice } from "../utility/helper";

const notifiedSignalKeys = new Set();
const lastLiveSide = new Map();

function coinKey(value) {
  return String(value || "")
    .split("/")[0]
    .toUpperCase();
}

function sideOf(value) {
  const side = String(value || "").split("/")[0];
  return side === "Buy" || side === "Sell" ? side : "";
}

function liveSideOf(value) {
  const side = String(value || "").split("/")[0];
  if (side === "Buy" || side === "Sell" || side === "Hold") return side;
  return "";
}

function confidencePct(confidence) {
  const n = Number(confidence);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1 ? n : n * 100;
}

function levelText(value) {
  if (value == null || value === "") return "";
  const n = Number(String(value).replace("%", "").trim());
  if (!Number.isFinite(n) || n === 0) return "";
  return `${Number(n).toPrecision(2)}%`;
}

function levelPhrase(target, stop) {
  const parts = [];
  const targetText = levelText(target);
  const stopText = levelText(stop);
  if (targetText) parts.push(`target ${targetText}`);
  if (stopText) parts.push(`stop ${stopText}`);
  return parts.join(", ");
}

function boardCoinsFrom(kind, rows) {
  const coins = new Set();
  if (kind === "assistant") {
    (rows || []).forEach((row) => {
      const coin = coinKey(row?.SYMBOL_NAME);
      if (coin) coins.add(coin);
    });
    return coins;
  }
  (rows || []).forEach((item) => {
    const coin = coinKey(item?.coin);
    if (coin) coins.add(coin);
  });
  return coins;
}

function powerfulCopy(coin, side, target, stop) {
  const levels = levelPhrase(target, stop);
  return {
    title: `${coin} ${side.toLowerCase()} signal is powerful now`,
    body: levels
      ? `Strength 5 of 5. ${levels}`
      : "Strength 5 of 5",
  };
}

function collectHits(kind, rows) {
  const hits = [];
  const seenBoard = new Set();
  if (kind === "assistant") {
    (rows || []).forEach((row) => {
      const coin = coinKey(row?.SYMBOL_NAME);
      const side = sideOf(row?.TRADE_TYPE);
      const strength = Math.round(Number(row?.SIGNAL_STRENGTH));
      if (!coin || !side || strength !== 5) return;
      const key = `${coin}|${side}|board`;
      if (seenBoard.has(key)) return;
      seenBoard.add(key);
      hits.push({
        key,
        ...powerfulCopy(coin, side, row?.TAKE_PROFIT_PERCENT, row?.STOP_LOSS_PERCENT),
      });
    });
  } else {
    (rows || []).forEach((item) => {
      const coin = coinKey(item?.coin);
      if (!coin) return;
      Object.entries(item || {}).forEach(([name, payload]) => {
        if (name === "coin" || !payload || typeof payload !== "object") return;
        if (payload.data_gap) return;
        const side = sideOf(payload.suggestion);
        const strength = parseInt(payload["signal strength"], 10);
        if (!side || strength !== 5) return;
        const key = `${coin}|${side}|board`;
        if (seenBoard.has(key)) return;
        seenBoard.add(key);
        hits.push({ key, ...powerfulCopy(coin, side) });
      });
    });
  }
  return hits;
}

function collectLiveChanges(kind, rows, suggestions, timeframe, boardKey) {
  const hits = [];
  const boardCoins = boardCoinsFrom(kind, rows);
  const wanted = String(timeframe || "15m");
  (suggestions || []).forEach((item) => {
    const coin = coinKey(item?.symbol);
    const side = liveSideOf(item?.prediction);
    const itemTf = String(item?.timeframe || "15m");
    if (!coin || !side || !boardCoins.has(coin) || itemTf !== wanted) return;
    const stateKey = `${boardKey}|${coin}|${itemTf}`;
    const prev = lastLiveSide.get(stateKey);
    lastLiveSide.set(stateKey, side);
    if (prev !== "Hold" || (side !== "Buy" && side !== "Sell")) return;
    const action = side.toLowerCase();
    const pct = Math.round(confidencePct(item?.confidence));
    const strong = pct >= 75;
    const levels = levelPhrase(
      item?.position_params?.tp_percent,
      item?.position_params?.sl_percent,
    );
    const facts = [
      pct > 0 ? `${pct}% confidence` : "",
      levels,
    ].filter(Boolean);
    hits.push({
      key: `${stateKey}|${prev}>${side}|${item?.signal_timestamp || ""}`,
      title: strong
        ? `${coin} ${action} signal is powerful now`
        : `${coin} ${action} left Hold and is still weak`,
      body: [
        `${itemTf} live AI moved from Hold to a ${strong ? "strong" : "weak"} ${action}`,
        ...facts,
      ].join(". "),
    });
  });
  return hits;
}

function present(hits, page) {
  if (hits.length === 1) {
    return { title: hits[0].title, body: `${hits[0].body}. ${page}` };
  }
  return {
    title: `${hits.length} signal changes`,
    body: `${hits
      .slice(0, 2)
      .map((item) => item.title)
      .join(" · ")}. ${page}`,
  };
}

function usePowerfulSignalNotice({ kind, page, boardKey, rows, suggestions, timeframe }) {
  const [permission, setPermission] = useState(() =>
    typeof Notification === "undefined" ? "denied" : Notification.permission,
  );

  useEffect(() => {
    promptNoticeAccess({ onGranted: () => setPermission("granted") });
  }, []);

  useEffect(() => {
    if (permission !== "granted" || !rows?.length) return;
    const hits = [
      ...collectHits(kind, rows),
      ...collectLiveChanges(kind, rows, suggestions, timeframe, boardKey),
    ];
    const fresh = hits.filter((item) => !notifiedSignalKeys.has(`${boardKey}|${item.key}`));
    if (!fresh.length) return;
    fresh.forEach((item) => notifiedSignalKeys.add(`${boardKey}|${item.key}`));
    const notice = present(fresh, page);
    if (!notice.title) return;
    queueBrowserNotice(notice.title, { body: notice.body });
  }, [permission, kind, page, boardKey, rows, suggestions, timeframe]);
}

export default usePowerfulSignalNotice;
